<script setup>
import { computed, h } from 'vue';
import { useI18n } from 'vue-i18n';
import SummaryReportLink from './SummaryReportLink.vue';
import SummaryArchiveLink from './SummaryArchiveLink.vue';
import Pagination from 'dashboard/components/table/Pagination.vue';
import { formatReportDuration } from '../constants';
import { useReportTablePagination } from '../composables/useReportTablePagination';
import { useReportTableSort } from '../composables/useReportTableSort';

const props = defineProps({
  labelMetrics: {
    type: Array,
    default: () => [],
  },
  withoutTagsCount: {
    type: Number,
    default: 0,
  },
  withoutTagsReport: {
    type: Object,
    default: null,
  },
  archiveContext: {
    type: Object,
    default: () => ({}),
  },
});
const WITHOUT_TAGS_ID = 0;

const { t } = useI18n();

const labelArchiveContext = computed(() => ({
  ...props.archiveContext,
  reportType: 'label',
}));

const renderCountCell = (row, metricKey, rawValue) =>
  h(SummaryArchiveLink, {
    value: row.value,
    metricKey,
    row,
    archiveContext: labelArchiveContext.value,
    rawValue,
  });

const renderMetricCell = (row, displayValue, metricKey, rawValue) =>
  h(SummaryArchiveLink, {
    value: displayValue,
    metricKey,
    row,
    archiveContext: labelArchiveContext.value,
    rawValue,
  });

const formatCount = value => (value ? value.toLocaleString() : '--');
const formatPercent = value =>
  value === null || value === undefined || value === '' ? '--' : `${value}%`;

const mapTagRow = metric => ({
  id: metric.id,
  name: metric.name,
  type: 'label',
  value: formatCount(metric.conversationsCount),
  share: formatPercent(metric.tagShare),
  botRate: formatPercent(metric.botResolutionRate),
  medianResolution: formatReportDuration(metric.medianResolutionTime, t),
  rawConversations: metric.conversationsCount,
  sort_value: metric.conversationsCount || 0,
  sort_share: Number(metric.tagShare) || 0,
  sort_botRate: Number(metric.botResolutionRate) || 0,
  sort_medianResolution: Number(metric.medianResolutionTime) || 0,
  rawBotResolved:
    metric.botResolutionRate > 0 ? metric.conversationsCount || 1 : 0,
  rawResolved: metric.resolvedConversationsCount || 0,
});

const tagRows = computed(() => {
  const taggedRows = props.labelMetrics
    .filter(metric => metric.id !== WITHOUT_TAGS_ID)
    .sort(
      (left, right) =>
        (right.conversationsCount || 0) - (left.conversationsCount || 0)
    )
    .slice(0, 10)
    .map(mapTagRow);

  const withoutReport = props.withoutTagsReport;
  const withoutCount =
    withoutReport?.conversationsCount || props.withoutTagsCount || 0;

  if (withoutCount > 0) {
    taggedRows.push({
      id: WITHOUT_TAGS_ID,
      name: t('SUMMARY_REPORTS.CARDS.LABEL.WITHOUT_TAGS'),
      type: 'label',
      value: formatCount(withoutCount),
      share: formatPercent(withoutReport?.tagShare),
      botRate: formatPercent(withoutReport?.botResolutionRate),
      medianResolution: formatReportDuration(
        withoutReport?.medianResolutionTime,
        t
      ),
      rawConversations: withoutCount,
      sort_value: withoutCount,
      sort_share: Number(withoutReport?.tagShare) || 0,
      sort_botRate: Number(withoutReport?.botResolutionRate) || 0,
      sort_medianResolution: Number(withoutReport?.medianResolutionTime) || 0,
      rawBotResolved:
        withoutReport?.botResolutionRate > 0 ? withoutCount || 1 : 0,
      rawResolved: withoutReport?.resolvedConversationsCount || 0,
    });
  }

  return taggedRows;
});

const { sortKey, sortDir, searchQuery, toggleSort, sortedRows } =
  useReportTableSort(tagRows, 'value', {
    numericKeys: ['value', 'share', 'botRate', 'medianResolution'],
  });

const { table, pagedRows, getPageSize, handlePageSizeChange } =
  useReportTablePagination(sortedRows, 'overview_tags_table_page_size');

const columns = computed(() => [
  {
    key: 'name',
    label: t('SUMMARY_REPORTS.LABEL'),
    cell: row =>
      h(SummaryReportLink, {
        row: { original: row },
        compact: true,
      }),
  },
  {
    key: 'value',
    label: t('SUMMARY_REPORTS.CONVERSATIONS'),
    cell: row =>
      renderCountCell(row, 'conversationsCount', row.rawConversations),
  },
  {
    key: 'share',
    label: t('SUMMARY_REPORTS.HEADER_SHORT.TAG_SHARE'),
    cell: row => h('span', { class: 'tabular-nums' }, row.share),
  },
  {
    key: 'botRate',
    label: t('SUMMARY_REPORTS.HEADER_SHORT.BOT_RESOLUTION_RATE'),
    cell: row =>
      renderMetricCell(
        row,
        row.botRate,
        'botResolvedCount',
        row.rawBotResolved
      ),
  },
  {
    key: 'medianResolution',
    label: t('SUMMARY_REPORTS.HEADER_SHORT.MEDIAN_RESOLUTION_TIME'),
    cell: row =>
      renderMetricCell(
        row,
        row.medianResolution,
        'resolutionsCount',
        row.medianResolution && row.medianResolution !== '--' ? 1 : 0
      ),
  },
]);
</script>

<template>
  <section
    class="relative mt-5 px-2 py-2 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
  >
    <h3 class="px-2 py-2 text-base font-medium text-n-slate-12">
      {{ $t('OVERVIEW_REPORTS.TAGS_TABLE.TITLE') }}
    </h3>
    <div class="mb-2 flex justify-end px-2">
      <input
        v-model="searchQuery"
        type="search"
        class="w-full max-w-xs rounded-lg border border-n-weak bg-n-solid-1 px-3 py-1.5 text-sm text-n-slate-12 outline-none focus:border-n-brand"
        :placeholder="$t('SUMMARY_REPORTS.TABLE_SEARCH')"
      />
    </div>
    <div class="overflow-x-auto">
      <table class="w-full table-fixed text-xs">
        <thead>
          <tr class="border-b border-n-weak text-[10px] text-n-slate-11">
            <th
              v-for="column in columns"
              :key="column.key"
              class="px-2 py-2 text-left font-medium cursor-pointer select-none"
              @click="toggleSort(column.key)"
            >
              {{ column.label }}
              <span v-if="sortKey === column.key">{{
                sortDir === 'desc' ? '↓' : '↑'
              }}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in pagedRows"
            :key="row.id"
            class="border-b border-n-weak/60 last:border-b-0"
          >
            <td
              v-for="column in columns"
              :key="column.key"
              class="px-2 py-2 text-center text-n-slate-12"
              :class="{ 'text-left': column.key === 'name' }"
            >
              <component :is="column.cell(row)" />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <Pagination
      v-if="tagRows.length"
      class="mt-2 px-2"
      :table="table"
      show-page-size-selector
      :default-page-size="getPageSize()"
      @page-size-change="handlePageSizeChange"
    />
    <p
      v-if="!tagRows.length"
      class="px-2 py-6 text-center text-sm text-n-slate-11"
    >
      {{ $t('SUMMARY_REPORTS.CHARTS.NO_DATA') }}
    </p>
  </section>
</template>
